import { useEffect } from 'react';

import cx from 'classnames';

import { updateOrderType } from 'actions/placeorder';
import { isAuthenticated } from 'actions/user';
import { IS_DEMO } from 'constants/constants';
import { PLACE_ORDER_TYPE, TRADE_PAGE_VERIFICATION_FLAGS } from 'constants/enums';
import useTotalAccountEquity from 'hooks/componentHooks/useTotalAccountEquity';
import useKYC from 'hooks/useKYC';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import i18n from 'i18n/config';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import { appDrawerSelector, showOrderOnChartSelector } from 'selectors/otherSelectors';
import { quantityInputHasErrorSelector } from 'selectors/placeOrderSelector';
import { isOnlineSelector, isSocketReconnecting } from 'selectors/tradeSelectors';
import { selectTradePagesVerificationMessageFlag } from 'selectors/user';
import { useAppDispatch, useAppSelector } from 'storeHooks';

import KycRefreshExpired from '../../KycRefreshExpiredPlaceOrder/KycRefreshExpired';
import ConfirmationModals from '../ConfirmationModals';
import Form from '../Form';
import QuantityInput from '../QuantityInput';
import VerifyButton from '../SubmitButton/VerifyButton';
import BuyButton from './BuyButton';
import styles from './ChartScreen.module.scss';
import ErrorMessage from './ErrorMessages';
import SellButton from './SellButton';

const ChartScreen = () => {
  const isLoggedIn = useAppSelector(({ user }) => isAuthenticated(user));
  const { isVerified, isReduceOnlyEnable, isKycRefreshRequired } = useKYC();
  const isInternetConnected = useAppSelector(isOnlineSelector);
  const isSocketDisconnected = useAppSelector(isSocketReconnecting);
  const showOnChartScreen = useAppSelector(showOrderOnChartSelector);
  const totalBalance = useTotalAccountEquity();
  const dispatch = useAppDispatch();
  const quantityInputHasError = useAppSelector(quantityInputHasErrorSelector);
  let placeHolderValue = i18n.t('trading:quantity');
  const { whenBelowXss, isDesktop } = useCustomMediaQuery();
  const { activeTab } = useAppSelector(appDrawerSelector);

  const verificationMessageFlag = useAppSelector(selectTradePagesVerificationMessageFlag);
  const showGetVerified =
    verificationMessageFlag === TRADE_PAGE_VERIFICATION_FLAGS.GET_VERIFIED;

  if (whenBelowXss) {
    placeHolderValue = ' ';
  }

  const isVerifiedUser = isVerified || isKycRefreshRequired || IS_DEMO;

  useEffect(() => {
    if (!isDesktop) {
      dispatch(
        updateOrderType(PLACE_ORDER_TYPE.MARKET, { shouldUpdatePreference: false })
      );
    }
  }, [dispatch, isDesktop]);

  if (
    !isLoggedIn ||
    !showOnChartScreen ||
    activeTab !== APP_DRAWER_TAB_ID_MAPPING.CHART_ORDERBOOK ||
    isDesktop
  ) {
    return null;
  }

  // if the user is verified or reduceonly mode is enabled or if the user is Indian and level3 verification is done - show Buy button
  // else show Get Verified button
  const isTradingEnabled =
    (isVerifiedUser && totalBalance) || IS_DEMO|| isReduceOnlyEnable;

  // TODO: move global exchange logic to  selector
  if (isReduceOnlyEnable && !showGetVerified) {
    return <KycRefreshExpired />;
  }

  return (
    (<Form
      className={cx('flex-set', styles.form, {
        [styles.unVerifiedContainer]: !isTradingEnabled || showGetVerified,
        [styles.verifiedContainer]: isTradingEnabled,
      })}
    >
      {isTradingEnabled && !showGetVerified && (
        <>
          <div className={styles.input} data-palette="ChartScreen">
            <QuantityInput placeholder={placeHolderValue} />
            <ErrorMessage />
          </div>
          <SellButton
            disabled={Boolean(
              !isInternetConnected || isSocketDisconnected || quantityInputHasError
            )}
          />
          <BuyButton
            disabled={Boolean(
              !isInternetConnected || isSocketDisconnected || quantityInputHasError
            )}
          />
        </>
      )}
      {(!isTradingEnabled || showGetVerified) && <VerifyButton />}
      <ConfirmationModals />
    </Form>)
  );
};

export default ChartScreen;
